import { useEffect, useRef } from 'react';
import useAppActivation from './useAppActivation';
import { useRouter } from './useRouter';
import useToast from './useToast';
import { Severity } from 'Contexts/RestaurantContext';
import { VisitWish, visitWishApi, OPEN_STATUSES } from 'App/ReservationBook/Screens/VisitWishes/visitWishApi';
import { coversDate } from 'App/ReservationBook/Screens/VisitWishes/wishFilters';
import { visitWishEditor } from 'utils/visitWishReservation';
import { EditReservation } from 'types/reservations';
import { db } from 'utils/firebase';
import { timeToBlock } from 'utils/helper';

export default function useVisitWishEditorEntry(
  restaurantId: string,
  openEditor: (editor: EditReservation | null) => void,
  selectReservation: (id?: string) => void
) {
  const activation = useAppActivation('visit_wishes', restaurantId);
  const router = useRouter();
  const toast = useToast();
  const callbacks = useRef({ openEditor, selectReservation, toast, router });
  callbacks.current = { openEditor, selectReservation, toast, router };
  const search = router.location?.search ?? '';
  const pathname = router.pathname;
  useEffect(() => {
    const query = new URLSearchParams(search);
    const id = query.get('visitWishId');
    const prefix = `/${restaurantId}/reservations/`;
    const date = pathname.startsWith(prefix) ? pathname.slice(prefix.length).split('/')[0] : '';
    if (activation !== 'active' || !id || !/^[\w-]{1,64}$/.test(id) || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    let cancelled = false;
    const clear = () => {
      query.delete('visitWishId');
      callbacks.current.router.replace({ pathname, search: query.toString() ? `?${query}` : '' });
    };
    const gone = (error: any) => {
      if (error?.response?.status === 404) return null;
      throw error;
    };
    visitWishApi.get(restaurantId, id).catch(gone).then(async (wish: VisitWish | null) => {
      if (cancelled) return;
      if (!wish || !OPEN_STATUSES.includes(wish.status)) {
        callbacks.current.toast('Diese Anfrage ist für diesen Tag nicht mehr verfügbar.', Severity.WARNING);
        clear();
        return;
      }
      const linked = wish.staffReservation;
      if (linked) {
        if (linked.date !== date) {
          callbacks.current.router.replace({ pathname: `/${restaurantId}/reservations/${linked.date}/tablePlan`, search });
          return;
        }
        if (linked.state === 'protection_pending') {
          callbacks.current.openEditor(null);
          callbacks.current.selectReservation(linked.id);
          clear();
          return;
        }
        const saved = (await db.collection('requestsV03').doc(linked.id).get()).data();
        if (cancelled) return;
        if (!saved || saved.restaurant !== restaurantId || saved.visitWishId !== wish.id || saved.deleted || saved.status === 'failed' || typeof saved.validTill !== 'number' || saved.validTill <= Date.now()) {
          callbacks.current.toast('Die Vorbereitung wurde inzwischen geändert. Bitte öffnen Sie die Anfrage erneut.', Severity.WARNING);
          clear();
          return;
        }
        if (saved.date !== date) {
          callbacks.current.router.replace({ pathname: `/${restaurantId}/reservations/${saved.date}/tablePlan`, search });
          return;
        }
        callbacks.current.selectReservation();
        callbacks.current.openEditor({
          ...visitWishEditor(wish, saved.date, linked.id),
          page: 1,
          visitWishHoldCreated: true,
          guest: { ...saved.guest, ...(!saved.guest?.id ? { createNew: true } : {}) },
          guests: saved.guests,
          occassion: saved.occassion,
          tables: saved.tables,
          tableStr: saved.tableStr,
          space: saved.space,
          reservationLength: saved.reservationLength,
          time: saved.time,
          customId: saved.customId,
          timeSlot: { time: saved.time, block: timeToBlock(saved.time), reservationLength: saved.reservationLength, blockedSlots: 0, combinations: [], maxGuestsReached: false, totalSlots: 0 },
        });
        clear();
        return;
      }
      if (!coversDate(wish, date)) {
        callbacks.current.toast('Diese Anfrage ist für diesen Tag nicht mehr verfügbar.', Severity.WARNING);
        clear();
        return;
      }
      callbacks.current.selectReservation();
      callbacks.current.openEditor(visitWishEditor(wish, date, db.collection('requestsV03').doc().id));
      clear();
    }).catch(() => {
      if (!cancelled) {
        callbacks.current.toast('Die Anfrage konnte nicht geladen werden. Bitte öffnen Sie sie erneut.', Severity.ERROR);
        clear();
      }
    });
    return () => { cancelled = true; };
  }, [restaurantId, activation, pathname, search]);
}
